import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL } from '../config';

interface Project {
  id: string;
  name: string;
  total_investment: number;
}

interface ProjectsResponse {
  projects: Project[];
  total_budget: number;
  remaining_budget: number;
}

interface MyInvestmentResponse {
  venue_id?: string | null;
  investments: Record<string, number>;
  scores: Record<string, number>;
  is_voted: boolean;
  member_start_project_id?: string | null;
}

type InvestmentMap = Record<string, number>;
type ScoreMap = Record<string, number>;
type SubmitMode = 'draft' | 'lock' | null;
type JudgeTab = 'score' | 'invest';
type GuideFormat = 'text' | 'markdown';

interface AuthUser {
  user_id: string;
  role: 'super_admin' | 'admin' | 'judge' | 'investor';
  display_name: string;
  venue_id?: string | null;
  phone?: string | null;
}

interface JudgeUIProps {
  authToken: string;
  authUser: AuthUser;
  venueId: string;
  venueName?: string;
  isLocked: boolean;
  guideTitle?: string;
  guideContent?: string;
  guideFormat?: GuideFormat;
  onSubmitted: () => Promise<void>;
}

function JudgeUI({ authToken, authUser, venueId, venueName, isLocked, guideTitle = '', guideContent = '', guideFormat = 'text', onSubmitted }: JudgeUIProps) {
  const STEP_AMOUNT = 1000;
  const SCORE_STEP = 1;
  const AUTO_SAVE_DELAY_MS = 3000;
  const HOLD_DELAY_MS = 280;
  const HOLD_INTERVAL_MS = 80;
  const canScore = authUser.role === 'judge';

  // Helper function to clean display_name by removing "name::" prefix
  const cleanDisplayName = (displayName: string | undefined): string => {
    if (!displayName) return '';
    if (displayName.startsWith('name::')) {
      return displayName.substring(6); // Remove "name::" prefix
    }
    return displayName;
  };

  const [projects, setProjects] = useState<Project[]>([]);
  const [totalBudget, setTotalBudget] = useState(10000);
  const [investments, setInvestments] = useState<InvestmentMap>({});
  const [scores, setScores] = useState<ScoreMap>({});
  const submittedScores = useMemo<ScoreMap>(() => (canScore ? scores : {}), [canScore, scores]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<JudgeTab>(canScore ? 'score' : 'invest');
  const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setSubmitMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState<SubmitMode>(null);
  const [isLockConfirmOpen, setIsLockConfirmOpen] = useState(false);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(true);
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);

  const submittingRef = useRef(false);
  const holdTimeoutRef = useRef<number | null>(null);
  const holdIntervalRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const suppressNextAutoSaveRef = useRef(true);
  const lastSubmittedSnapshotRef = useRef<string>('');
  const currentSubmitModeRef = useRef<SubmitMode>(null);
  const pendingLockAfterDraftRef = useRef(false);

  const selectedProject = useMemo(() => {
    if (!projects.length) {
      return null;
    }
    return projects.find((project) => project.id === selectedProjectId) || projects[0];
  }, [projects, selectedProjectId]);

  const fetchProjects = useCallback(async (showLoader = false): Promise<void> => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const response = await axios.get<ProjectsResponse>(`${API_BASE_URL}/api/projects`, {
        params: { venue_id: venueId, role: authUser.role },
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      const projectList = response.data.projects;

      setProjects(projectList);
      setTotalBudget(response.data.total_budget || 10000);

      const initialInvestments: InvestmentMap = {};
      const initialScores: ScoreMap = {};
      let preferredStartProjectId: string | null = null;
      projectList.forEach((project) => {
        initialInvestments[project.id] = 0;
        initialScores[project.id] = 0;
      });

      try {
        const myInvestmentResp = await axios.get<MyInvestmentResponse>(`${API_BASE_URL}/api/judges/my-investment`, {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        });

        const savedInvestments = myInvestmentResp.data.investments || {};
        const savedScores = myInvestmentResp.data.scores || {};
        preferredStartProjectId = myInvestmentResp.data.member_start_project_id || null;
        projectList.forEach((project) => {
          if (typeof savedInvestments[project.id] === 'number') {
            initialInvestments[project.id] = savedInvestments[project.id];
          }
          if (typeof savedScores[project.id] === 'number') {
            initialScores[project.id] = Math.max(0, Math.min(100, Math.round(savedScores[project.id])));
          }
        });
      } catch (savedErr) {
        console.warn('Unable to load saved judge data:', savedErr);
      }

      suppressNextAutoSaveRef.current = true;
      setInvestments(initialInvestments);
      setScores(initialScores);
      if (projectList.length > 0) {
        setSelectedProjectId((prev) => {
          if (prev && projectList.some((project) => project.id === prev)) {
            return prev;
          }
          if (preferredStartProjectId && projectList.some((project) => project.id === preferredStartProjectId)) {
            return preferredStartProjectId;
          }
          return projectList[0].id;
        });
      }
      lastSubmittedSnapshotRef.current = JSON.stringify({
        investments: initialInvestments,
        scores: initialScores,
      });
      setError(null);
    } catch (err: unknown) {
      setError('載入專案資料發生錯誤，請聯絡系統管理員');
      console.error('Error fetching projects:', err);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [authToken, authUser.role, venueId]);

  useEffect(() => {
    fetchProjects(true);
  }, [fetchProjects]);

  useEffect(() => {
    if (!selectedProjectId && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    if (!canScore && activeTab !== 'invest') {
      setActiveTab('invest');
    }
  }, [activeTab, canScore]);

  const getTotalInvested = () => {
    return Object.values(investments).reduce((a, b) => a + b, 0);
  };

  const getRemainingBudget = () => {
    return totalBudget - getTotalInvested();
  };

  const isDraftValid = useCallback(() => {
    if (projects.length === 0) {
      return false;
    }
    const totalInvested = Object.values(investments).reduce((a, b) => a + b, 0);
    const scoreValid = !canScore || Object.values(scores).every((score) => score >= 0 && score <= 100);
    return totalInvested <= totalBudget && scoreValid;
  }, [canScore, investments, projects, scores, totalBudget]);

  const isFormValid = useCallback(() => {
    return isDraftValid();
  }, [isDraftValid]);

  const hasNegativeInvestment = useCallback(() => {
    return Object.values(investments).some((amount) => amount < 0);
  }, [investments]);

  const hasInvalidScore = useCallback(() => {
    return canScore && Object.values(scores).some((score) => score < 0 || score > 100);
  }, [canScore, scores]);

  const stopContinuousAdjust = useCallback(() => {
    if (holdTimeoutRef.current !== null) {
      window.clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current !== null) {
      window.clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);

  useEffect(() => stopContinuousAdjust, [stopContinuousAdjust]);

  const adjustInvestmentByStep = useCallback((projectId: string, direction: -1 | 1) => {
    if (isSubmitting || isLocked) {
      return;
    }

    setSubmitMessage(null);
    setInvestments((prev) => {
      const currentAmount = prev[projectId] || 0;
      const totalInvested = Object.values(prev).reduce((sum, amount) => sum + amount, 0);
      const totalWithoutCurrent = totalInvested - currentAmount;
      const maxAllowedForProject = Math.max(0, totalBudget - totalWithoutCurrent);
      const nextAmount = direction > 0
        ? Math.min(maxAllowedForProject, currentAmount + STEP_AMOUNT)
        : Math.max(0, currentAmount - STEP_AMOUNT);

      if (nextAmount === currentAmount) {
        return prev;
      }

      return {
        ...prev,
        [projectId]: nextAmount,
      };
    });
  }, [isLocked, isSubmitting, totalBudget]);

  const startContinuousAdjust = useCallback((projectId: string, direction: -1 | 1) => {
    stopContinuousAdjust();
    adjustInvestmentByStep(projectId, direction);
    holdTimeoutRef.current = window.setTimeout(() => {
      holdIntervalRef.current = window.setInterval(() => {
        adjustInvestmentByStep(projectId, direction);
      }, HOLD_INTERVAL_MS);
    }, HOLD_DELAY_MS);
  }, [adjustInvestmentByStep, stopContinuousAdjust]);

  const handleInvestmentChange = (projectId: string, value: string) => {
    const numValue = Math.max(0, parseInt(value, 10) || 0);
    setSubmitMessage(null);
    setInvestments((prev) => ({
      ...prev,
      [projectId]: numValue
    }));
  };

  const handleScoreChange = (projectId: string, value: string) => {
    const parsed = parseInt(value, 10);
    const score = Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
    setSubmitMessage(null);
    setScores((prev) => ({
      ...prev,
      [projectId]: score,
    }));
  };

  const adjustScoreByStep = (projectId: string, direction: -1 | 1) => {
    if (isSubmitting || isLocked) {
      return;
    }
    setScores((prev) => {
      const current = prev[projectId] || 0;
      const next = Math.max(0, Math.min(100, current + direction * SCORE_STEP));
      if (next === current) {
        return prev;
      }
      return {
        ...prev,
        [projectId]: next,
      };
    });
  };

  const handleSubmit = useCallback(async (lockSubmission: boolean, silent = false): Promise<void> => {
    if (submittingRef.current || isSubmitting) {
      if (lockSubmission && currentSubmitModeRef.current === 'draft') {
        pendingLockAfterDraftRef.current = true;
      }
      return;
    }
    submittingRef.current = true;
    currentSubmitModeRef.current = lockSubmission ? 'lock' : 'draft';

    if (isLocked) {
      if (!silent) {
        setError('已鎖定，無法再次提交');
      }
      submittingRef.current = false;
      return;
    }

    if (lockSubmission && !isFormValid()) {
      if (!silent) {
        setError(
          canScore
            ? `正式上傳驗證失敗：請確認投資總額不超過 ${totalBudget.toLocaleString()} 元，且每一組都已填入投資與評分`
            : `正式上傳驗證失敗：請確認投資總額不超過 ${totalBudget.toLocaleString()} 元。`
        );
      }
      submittingRef.current = false;
      return;
    }

    if (!lockSubmission && !isDraftValid()) {
      if (!silent) {
        setError(
          canScore
            ? `暫存失敗：投資總額不可超過 ${totalBudget.toLocaleString()} 元，且每項評分須介於 0~100。`
            : `暫存失敗：投資總額不可超過 ${totalBudget.toLocaleString()} 元。`
        );
      }
      submittingRef.current = false;
      return;
    }

    for (const [projectId, amount] of Object.entries(investments)) {
      if (amount < 0) {
        if (!silent) {
          setError(`投資金額不能小於 0，請確認 ${projectId} 的投資金額`);
        }
        submittingRef.current = false;
        return;
      }
    }

    for (const [projectId, score] of Object.entries(submittedScores)) {
      if (score < 0 || score > 100) {
        if (!silent) {
          setError(`評分必須介於 0 到 100 之間，請確認 ${projectId} 的評分`);
        }
        submittingRef.current = false;
        return;
      }
    }

    try {
      setSubmitMode(lockSubmission ? 'lock' : 'draft');
      if (lockSubmission) {
        setIsSubmitting(true);
      }
      setSubmitMessage(null);
      await axios.post(`${API_BASE_URL}/api/submit_investment`, {
        investments,
        scores: submittedScores,
        lock_submission: lockSubmission,
      }, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

  setSubmitMessage(lockSubmission ? '最後結果已上傳並鎖定，無法再修改' : `${canScore ? '評分與投資' : '投資'}已自動暫存，可繼續調整。`);
      setError(null);
  lastSubmittedSnapshotRef.current = JSON.stringify({ investments, scores: submittedScores });
      await onSubmitted();
    } catch (err: unknown) {
      if (!silent) {
        setError('提交資料失敗，發生錯誤，請聯絡系統管理員');
      }
      setSubmitMessage(null);
      console.error('Error submitting judge payload:', err);
    } finally {
      const finishedMode = currentSubmitModeRef.current;
      submittingRef.current = false;
      if (lockSubmission) {
        setIsSubmitting(false);
      }
      setSubmitMode(null);
      currentSubmitModeRef.current = null;

      if (finishedMode === 'draft' && pendingLockAfterDraftRef.current) {
        pendingLockAfterDraftRef.current = false;
        void handleSubmit(true);
      }
    }
  }, [authToken, canScore, investments, isDraftValid, isFormValid, isLocked, isSubmitting, onSubmitted, submittedScores, totalBudget]);

  useEffect(() => {
    if (autoSaveTimeoutRef.current !== null) {
      window.clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    if (suppressNextAutoSaveRef.current) {
      suppressNextAutoSaveRef.current = false;
      return;
    }

    if (isLocked || loading || projects.length === 0) {
      return;
    }

    const snapshot = JSON.stringify({ investments, scores: submittedScores });
    if (snapshot === lastSubmittedSnapshotRef.current) {
      return;
    }

    autoSaveTimeoutRef.current = window.setTimeout(() => {
      void handleSubmit(false, true);
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (autoSaveTimeoutRef.current !== null) {
        window.clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [handleSubmit, investments, isLocked, loading, projects.length, submittedScores]);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current !== null) {
        window.clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="container judge-page-loading" role="status" aria-live="polite" aria-label="正在載入專案表">
        <span className="judge-upload-spinner judge-page-loading-spinner" aria-hidden="true" />
        <p className="judge-upload-status judge-page-loading-text">
          正在載入專案表
          <span className="dot dot-1" aria-hidden="true">.</span>
          <span className="dot dot-2" aria-hidden="true">.</span>
          <span className="dot dot-3" aria-hidden="true">.</span>
        </p>
      </div>
    );
  }

  const currentProject = selectedProject;
  const currentProjectIndex = currentProject ? projects.findIndex((p) => p.id === currentProject.id) : -1;
  const hasPrev = projects.length > 1;
  const hasNext = projects.length > 1;

  const goToPrev = () => {
    if (!hasPrev || currentProjectIndex < 0) {
      return;
    }
    const prevIndex = (currentProjectIndex - 1 + projects.length) % projects.length;
    setSelectedProjectId(projects[prevIndex].id);
  };
  const goToNext = () => {
    if (!hasNext || currentProjectIndex < 0) {
      return;
    }
    const nextIndex = (currentProjectIndex + 1) % projects.length;
    setSelectedProjectId(projects[nextIndex].id);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) goToNext();
    else goToPrev();
  };

  const currentInvestment = currentProject ? investments[currentProject.id] || 0 : 0;
  const currentScore = currentProject ? scores[currentProject.id] || 0 : 0;
  const totalWithoutCurrent = getTotalInvested() - currentInvestment;
  const maxAllowedForCurrent = Math.max(0, totalBudget - totalWithoutCurrent);
  const canDecrease = currentInvestment > 0 && !isSubmitting && !isLocked;
  const canIncrease = currentInvestment < maxAllowedForCurrent && !isSubmitting && !isLocked;
  const normalizedGuideContent = guideContent.trim();
  const showGuideButton = canScore && normalizedGuideContent.length > 0;
  const tabSwitchLayoutClass = showGuideButton
    ? (canScore ? 'has-guide-score' : 'has-guide-invest')
    : (!canScore ? 'single-invest' : '');
  const resolvedGuideTitle = (guideTitle || '').trim() || '評分與投資說明';
  const welcomeTitle = canScore ? '📋 評審使用說明' : '📋 投資客使用說明';
  const welcomeGuideItems = canScore
    ? [
      '請對各組進行評分與投資。',
      '評分與投資完成後，按下最終鎖定提交結果。',
      '鎖定後將無法再更改評分與投資。',
      '若需修改，請聯絡現場工作人員協助解鎖。',
    ]
    : [
      '請依各組表現分配您的投資金額。',
      '確認投資完成後，按下最終鎖定提交結果。',
      '鎖定後將無法再更改投資金額。',
      '若需修改，請聯絡現場工作人員協助解鎖。',
    ];
  const welcomeActionLabel = canScore ? '我知道了，開始評分' : '我知道了，開始投資';

  return (
    <div className="judge-ui container">
      {isWelcomeOpen && (
        <div className="judge-welcome-backdrop" role="dialog" aria-modal="true" aria-label="使用說明">
          <div className="judge-welcome-modal">
            <h3>{welcomeTitle}</h3>
            <ul className="judge-welcome-list">
              {welcomeGuideItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <div className="judge-welcome-actions">
              <button
                type="button"
                className="judge-welcome-ok-btn"
                onClick={() => setIsWelcomeOpen(false)}
              >
                {welcomeActionLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLockConfirmOpen && !isLocked && (
        <div className="judge-lock-confirm-backdrop" role="dialog" aria-modal="true" aria-label="確認正式上傳">
          <div className="judge-lock-confirm-modal">
            <h3>確認後無法復原</h3>
            <p>
              提交後將立即鎖定，無法再次評分與投資，後續無法修改任何資料。            </p>
            <p>
              {canScore
                ? '請確認已完成評分和投資。如有缺漏，可聯絡工作人員協助。'
                : '請確認已完成投資。如有缺漏，可聯絡工作人員協助。'}
            </p>
            <div className="judge-lock-confirm-actions">
              <button
                type="button"
                className="judge-lock-cancel-btn"
                onClick={() => setIsLockConfirmOpen(false)}
                disabled={isSubmitting}
              >
                取消
              </button>
              <button
                type="button"
                className="judge-lock-confirm-btn"
                onClick={() => {
                  if (autoSaveTimeoutRef.current !== null) {
                    window.clearTimeout(autoSaveTimeoutRef.current);
                    autoSaveTimeoutRef.current = null;
                  }
                  setIsLockConfirmOpen(false);
                  void handleSubmit(true);
                }}
                disabled={isSubmitting}
              >
                確認上傳並鎖定              </button>
            </div>
          </div>
        </div>
      )}

      {isProjectPickerOpen && (
        <div className="judge-project-picker-backdrop" role="dialog" aria-modal="true" aria-label="選擇專案組別" onClick={(e) => { if (e.target === e.currentTarget) setIsProjectPickerOpen(false); }}>
          <div className="judge-project-picker-modal">
            <div className="judge-project-picker-head">
              <h3>選擇要操作的組別</h3>
              <button
                type="button"
                className="judge-project-picker-close"
                onClick={() => setIsProjectPickerOpen(false)}
                aria-label="所有組別清單"
              >
                x
              </button>
            </div>
            <div className="judge-project-picker-list">
              {projects.map((project) => {
                const isActive = currentProject?.id === project.id;
                return (
                  <button
                    key={project.id}
                    type="button"
                    className={`judge-project-picker-item${isActive ? ' active' : ''}`}
                    onClick={() => {
                      setSelectedProjectId(project.id);
                      setIsProjectPickerOpen(false);
                    }}
                  >
                    <div className="judge-project-picker-title">{project.name}</div>
                    <div className="judge-project-picker-meta">
                      {canScore ? <span>評分：{(scores[project.id] || 0).toLocaleString()} 分</span> : null}
                      <span>投資：{(investments[project.id] || 0).toLocaleString()}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {isGuideModalOpen && showGuideButton && (
        <div
          className="judge-guide-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={resolvedGuideTitle}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsGuideModalOpen(false);
            }
          }}
        >
          <div className="judge-guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="judge-guide-modal-head">
              <h3>{resolvedGuideTitle}</h3>
              <button
                type="button"
                className="judge-guide-close"
                onClick={() => setIsGuideModalOpen(false)}
                aria-label="關閉說明視窗"
              >
                ×
              </button>
            </div>
            <div className="judge-guide-modal-body">
              {guideFormat === 'markdown' ? (
                <div className="judge-guide-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{normalizedGuideContent}</ReactMarkdown>
                </div>
              ) : (
                <div className="judge-guide-text">{normalizedGuideContent}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {isSubmitting && submitMode === 'lock' && (
        <div className="judge-upload-backdrop" role="status" aria-live="polite" aria-label="正在上傳評分與投資資料">
          <div className="judge-upload-modal">
            <span className="judge-upload-spinner" aria-hidden="true" />
            <h3>正在上傳最後結果</h3>
            <p className="judge-upload-status">
              資料傳送中，請稍候              <span className="dot dot-1" aria-hidden="true">.</span>
              <span className="dot dot-2" aria-hidden="true">.</span>
              <span className="dot dot-3" aria-hidden="true">.</span>
            </p>
          </div>
        </div>
      )}

      <section className="section judge-header">
        <h2>{canScore ? '評審評分與投資介面' : '投資者投資介面'}</h2>
        <p>您的身份：<strong>{cleanDisplayName(authUser.display_name)}</strong>（{authUser.role}）</p>
        {authUser.venue_id ? <p>您的會場：<strong>{venueName || authUser.venue_id}</strong></p> : null}
        <div className="judge-project-current-row">
          <span>目前組別</span>
          <div className="judge-project-nav-row">
            <button
              type="button"
              className="judge-project-nav-arrow"
              onClick={goToPrev}
              disabled={!hasPrev}
              aria-label="上一個"
            >&#8592;</button>
            <button type="button" className="judge-project-current-btn" onClick={() => setIsProjectPickerOpen(true)}>
              {currentProject ? currentProject.name : '尚無組別'}
              <span className="judge-project-switch-icon" aria-hidden="true">▼</span>
            </button>
            <button
              type="button"
              className="judge-project-nav-arrow"
              onClick={goToNext}
              disabled={!hasNext}
              aria-label="下一個"
            >&#8594;</button>
          </div>
        </div>
      </section>

      {error && <div className="error-toast" role="alert" aria-live="assertive">{error}</div>}

      <section className="section judge-form">
        <div
          className={`judge-tab-switch ${tabSwitchLayoutClass}`}
          role="tablist"
          aria-label="評分與投資選項"
        >
          {canScore && (
            <button
              type="button"
              className={`judge-tab-button ${activeTab === 'score' ? 'active' : ''}`}
              onClick={() => setActiveTab('score')}
              role="tab"
              aria-selected={activeTab === 'score'}
            >
              ⭐ 評分
            </button>
          )}
          <button
            type="button"
            className={`judge-tab-button ${activeTab === 'invest' ? 'active' : ''}`}
            onClick={() => setActiveTab('invest')}
            role="tab"
            aria-selected={activeTab === 'invest'}
          >
            💰 投資
          </button>
          {showGuideButton && (
            <button
              type="button"
              className="judge-tab-button judge-tab-help-button"
              onClick={() => setIsGuideModalOpen(true)}
              aria-label={`開啟${resolvedGuideTitle}`}
            >
              📖 {resolvedGuideTitle}
            </button>
          )}
        </div>

        {currentProject ? (
          <div
            className="investment-item judge-single-project-card"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="project-header">
              <label>{currentProject.name}</label>
            </div>

            {canScore && activeTab === 'score' && (
              <div className="judge-score-editor">
                <p className="judge-score-hint">請給此項目 0~100 分</p>
                <div className="judge-score-input-row">
                  <button
                    type="button"
                    className="investment-stepper-button minus"
                    onClick={() => adjustScoreByStep(currentProject.id, -1)}
                    disabled={isSubmitting || isLocked || currentScore <= 0}
                    aria-label={`增加 ${currentProject.name} 1 分`}
                  >
                    -1
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step={SCORE_STEP}
                    value={currentScore}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleScoreChange(currentProject.id, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    disabled={isSubmitting || isLocked}
                    className="investment-input judge-score-input"
                  />
                  <button
                    type="button"
                    className="investment-stepper-button plus"
                    onClick={() => adjustScoreByStep(currentProject.id, 1)}
                    disabled={isSubmitting || isLocked || currentScore >= 100}
                    aria-label={`減少 ${currentProject.name} 1 分`}
                  >
                    +1
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'invest' && (
              <>
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    step={STEP_AMOUNT}
                    value={currentInvestment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInvestmentChange(currentProject.id, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    disabled={isSubmitting || isLocked}
                    className="investment-input"
                  />
                  <span className="currency">元</span>
                </div>

                <div className="investment-stepper" aria-label={`${currentProject.name} 投資快速調整`}>
                  <div className="investment-stepper-status">
                    <span>每次調整 1000 元</span>
                    <strong>此項目最高可投 ${maxAllowedForCurrent.toLocaleString()}</strong>
                  </div>
                  <div className="investment-stepper-actions">
                    <button
                      type="button"
                      className="investment-stepper-button minus"
                      onPointerDown={() => startContinuousAdjust(currentProject.id, -1)}
                      onPointerUp={stopContinuousAdjust}
                      onPointerLeave={stopContinuousAdjust}
                      onPointerCancel={stopContinuousAdjust}
                      disabled={!canDecrease}
                      aria-label={`減少 ${currentProject.name} 1000 元`}
                    >
                      -1000
                    </button>
                    <button
                      type="button"
                      className="investment-stepper-button plus"
                      onPointerDown={() => startContinuousAdjust(currentProject.id, 1)}
                      onPointerUp={stopContinuousAdjust}
                      onPointerLeave={stopContinuousAdjust}
                      onPointerCancel={stopContinuousAdjust}
                      disabled={!canIncrease}
                      aria-label={`增加 ${currentProject.name} 1000 元`}
                    >
                      +1000
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Show score summary only during scoring */}
            {activeTab === 'score' && canScore && (
              <div className="judge-current-summary">
                <div><span>目前評分：</span><strong>{currentScore.toLocaleString()} 分</strong></div>
              </div>
            )}
            {/* Show investment summary only when not in scoring tab */}
            {activeTab !== 'score' && (
              <div className="judge-current-summary">
                <div><span>目前投資：</span><strong>${currentInvestment.toLocaleString()}</strong></div>
              </div>
            )}
          </div>
        ) : null}

        {/* Only show budget summary when in invest tab */}
        {activeTab !== 'score' && (
          <div className="budget-summary">
            <div className="summary-item">
              <span>已投入：</span>
              <strong>${getTotalInvested().toLocaleString()}</strong>
            </div>
            <div className="summary-item">
              <span>剩餘金額：</span>
              <strong className={getRemainingBudget() < 0 ? 'invalid' : 'valid'}>
                ${getRemainingBudget().toLocaleString()}
              </strong>
            </div>
            <div className="summary-item">
              <span>投資上限：</span>
              <strong>${totalBudget.toLocaleString()}</strong>
            </div>
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          {!isLocked && (getTotalInvested() > totalBudget || hasNegativeInvestment() || hasInvalidScore()) && (
            <div className="validation-message" style={{ marginBottom: 12 }}>
              {hasNegativeInvestment() ? <p>有投資金額不可小於 0 元，請修改各組的投資</p> : null}
              {canScore && hasInvalidScore() ? <p>有評分不介於 0 到 100 之間</p> : null}
              {getTotalInvested() > totalBudget ? (
                <p>有投資超出上限：超出<strong>${(getTotalInvested() - totalBudget).toLocaleString()}</strong> 元</p>
              ) : null}
            </div>
          )}

          {!isLocked && (
            <p className="judge-auto-save-note" style={{ marginBottom: 10 }}>
              {canScore ? '評分與投資完成後，系統將暫停操作 3 秒後才儲存' : '投資變動後，系統將暫停操作 3 秒後才儲存'}
            </p>
          )}

          <div className="judge-submit-actions" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="judge-action-button lock"
              onClick={() => setIsLockConfirmOpen(true)}
              disabled={!isFormValid() || isSubmitting || isLocked}
            >
              {isLocked ? '已鎖定並提交' : isSubmitting ? '上傳中...' : '最後確認'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default JudgeUI;

